import React from "react"
import { useFetcher } from "react-router"
import { PlusIcon, TrashIcon, XCircleIcon, SaveIcon, LoaderCircleIcon, Pencil } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { PopoverClose } from "@radix-ui/react-popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

import type { ActionResponse } from "@/utils/types"
import type { LoaderData } from "./brand-ui-config"

interface DataPointLabel {
  position: number
  name: string
  themes: string[]
  showEvidenceThemes: boolean
}

interface DataPoint {
  usage: string
  labels: DataPointLabel[]
}

export function DataPointsTab({ config, data }: { config: DataPoint[] | undefined; data: LoaderData }) {
  const [dataPoints, setDataPoints] = React.useState<DataPoint[]>(config ?? [])
  const [editingLabel, setEditingLabel] = React.useState<{ dpIndex: number; labelIndex: number } | null>(null)
  const [showAddDataPointDialog, setShowAddDataPointDialog] = React.useState(false)
  const [showAddLabelDialog, setShowAddLabelDialog] = React.useState<number | null>(null)

  const fetcher = useFetcher<ActionResponse>()

  React.useEffect(() => {
    setDataPoints(config ?? [])
  }, [config])

  const availableThemes = React.useMemo(() => {
    return data.evidenceThemeData?.map((theme) => theme.name) ?? []
  }, [data.evidenceThemeData])

  const addDataPoint = (dataPoint: DataPoint) => {
    setDataPoints((prev) => [...prev, dataPoint])
    setShowAddDataPointDialog(false)
  }

  const removeDataPoint = (index: number) => {
    setDataPoints((prev) => prev.filter((_, i) => i !== index))
  }

  const addLabel = (dpIndex: number, label: DataPointLabel) => {
    setDataPoints((prev) => prev.map((dp, i) => (i === dpIndex ? { ...dp, labels: [label, ...dp.labels] } : dp)))
    setShowAddLabelDialog(null)
  }

  const updateLabel = (dpIndex: number, labelIndex: number, label: DataPointLabel) => {
    setDataPoints((prev) =>
      prev.map((dp, i) =>
        i === dpIndex ? { ...dp, labels: dp.labels.map((l, li) => (li === labelIndex ? label : l)) } : dp,
      ),
    )
    setEditingLabel(null)
  }

  const removeLabel = (dpIndex: number, labelIndex: number) => {
    setDataPoints((prev) =>
      prev.map((dp, i) => (i === dpIndex ? { ...dp, labels: dp.labels.filter((_, li) => li !== labelIndex) } : dp)),
    )
  }

  const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const formData = new FormData()
    formData.append("action", "SAVE_SECTION")
    formData.append("organisation", data.loadedOrganisation)
    formData.append("brand", data.loadedBrand ?? "")
    formData.append("jsonPath", "$.dataPoints")
    formData.append("value", JSON.stringify(dataPoints))
    formData.append("concurrency", data.config?.concurrency ?? "")

    fetcher.submit(formData, { method: "post" })
  }

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <div className="mx-auto w-full space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Data Points Configuration</h2>
            <Button onClick={() => setShowAddDataPointDialog(true)} variant="outline" size="sm">
              <PlusIcon className="mr-2 h-4 w-4" />
              Add Data Point
            </Button>
          </div>

          {dataPoints.length === 0 ? (
            <div className="rounded-lg border-2 border-dashed border-border py-16 text-center text-muted-foreground">
              <p className="text-lg">No data points configured</p>
            </div>
          ) : (
            <div className="space-y-4">
              {dataPoints.map((dataPoint, dpIndex) => (
                <div key={dpIndex} className="rounded-lg border p-4">
                  <div className="mb-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold">Data Point {dpIndex + 1}</h3>
                      <Badge variant="outline">{dataPoint.usage}</Badge>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => setShowAddLabelDialog(dpIndex)} variant="outline" size="sm">
                        <PlusIcon className="mr-1 h-4 w-4" />
                        Add Label
                      </Button>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50">
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent side="top" className="w-80">
                          <div className="space-y-3">
                            <p className="text-sm font-medium">Delete Data Point?</p>
                            <p className="text-sm text-muted-foreground">
                              This will delete the entire data point and all its labels.
                            </p>
                            <p className="text-sm font-medium text-red-600">This action cannot be undone.</p>
                            <div className="flex items-center justify-end gap-3">
                              <PopoverClose asChild>
                                <Button variant="outline" size="sm">
                                  Cancel
                                </Button>
                              </PopoverClose>
                              <PopoverClose asChild>
                                <Button onClick={() => removeDataPoint(dpIndex)} variant="destructive" size="sm">
                                  Delete
                                </Button>
                              </PopoverClose>
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  {dataPoint.labels.length === 0 ? (
                    <div className="rounded border-2 border-dashed border-border py-8 text-center text-muted-foreground">
                      <p className="text-sm">No labels in this data point</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {dataPoint.labels.map((label, labelIndex) => (
                        <div key={labelIndex} className="rounded border p-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="mb-2 flex items-center gap-2">
                                <Badge variant="secondary">Position: {label.position}</Badge>
                                <span className="font-medium">{label.name}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Checkbox checked={label.showEvidenceThemes} disabled />
                                <span>Show Evidence Themes</span>
                              </div>
                              {label.themes.length > 0 && (
                                <div className="mt-2">
                                  <p className="mb-1 text-xs text-muted-foreground">Themes:</p>
                                  <div className="flex flex-wrap gap-1">
                                    {label.themes.map((theme, ti) => (
                                      <Badge key={ti} variant="outline" className="text-xs">
                                        {theme}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="ml-4 flex gap-2">
                              <Button
                                onClick={() => setEditingLabel({ dpIndex, labelIndex })}
                                variant="ghost"
                                size="sm"
                                className="text-muted-foreground hover:bg-secondary hover:text-foreground"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50">
                                    <TrashIcon className="h-4 w-4" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent side="top" className="w-80">
                                  <div className="space-y-3">
                                    <p className="text-sm font-medium">Delete Label?</p>
                                    <p className="text-sm text-muted-foreground">
                                      This will delete the label &quot;{label.name}&quot; from this data point.
                                    </p>
                                    <p className="text-sm font-medium text-red-600">This action cannot be undone.</p>
                                    <div className="flex items-center justify-end gap-3">
                                      <PopoverClose asChild>
                                        <Button variant="outline" size="sm">
                                          Cancel
                                        </Button>
                                      </PopoverClose>
                                      <PopoverClose asChild>
                                        <Button
                                          onClick={() => removeLabel(dpIndex, labelIndex)}
                                          variant="destructive"
                                          size="sm"
                                        >
                                          Delete
                                        </Button>
                                      </PopoverClose>
                                    </div>
                                  </div>
                                </PopoverContent>
                              </Popover>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex-shrink-0 border-t p-4">
        <div className="mx-auto flex w-full gap-3">
          <fetcher.Form onSubmit={handleSave} className="flex-1">
            <Button type="submit" disabled={fetcher.state !== "idle"} className="bg-info hover:bg-info/90">
              {fetcher.state !== "idle" && fetcher.formData?.get("action") === "SAVE_SECTION" && (
                <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
              )}
              <SaveIcon className="h-4 w-4" />
              Save Data Points
            </Button>
          </fetcher.Form>

          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="flex-1 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                disabled={fetcher.state !== "idle"}
              >
                {fetcher.state !== "idle" && fetcher.formData?.get("action") === "REMOVE_SECTION" && (
                  <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
                )}
                <XCircleIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Remove Section</span>
                <span className="sm:hidden">Remove</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent side="top" className="w-80">
              <div className="space-y-3">
                <p className="text-sm font-medium">Remove Data Points Configuration?</p>
                <p className="text-sm text-muted-foreground">
                  This will remove the entire data points configuration from this level.
                </p>
                <p className="text-sm font-medium text-red-600">This action cannot be undone.</p>
                <div className="flex items-center justify-end gap-3">
                  <PopoverClose asChild>
                    <Button variant="outline" size="sm">
                      Cancel
                    </Button>
                  </PopoverClose>
                  <PopoverClose asChild>
                    <fetcher.Form method="post">
                      <input type="hidden" name="action" value="REMOVE_SECTION" />
                      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
                      <input type="hidden" name="brand" value={data.loadedBrand} />
                      <input type="hidden" name="jsonPath" value="$.dataPoints" />
                      <input type="hidden" name="concurrency" value={data.config?.concurrency ?? ""} />
                      <Button type="submit" variant="destructive" size="sm" disabled={fetcher.state !== "idle"}>
                        {fetcher.state !== "idle" && <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />}
                        Remove
                      </Button>
                    </fetcher.Form>
                  </PopoverClose>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Add Data Point Dialog */}
      {showAddDataPointDialog && (
        <DataPointDialog
          open={showAddDataPointDialog}
          onClose={() => setShowAddDataPointDialog(false)}
          onSave={addDataPoint}
          title="Add Data Point"
        />
      )}

      {/* Add Label Dialog */}
      {showAddLabelDialog !== null && (
        <LabelDialog
          open={true}
          onClose={() => setShowAddLabelDialog(null)}
          onSave={(label) => addLabel(showAddLabelDialog, label)}
          availableThemes={availableThemes}
          title="Add Label"
        />
      )}

      {/* Edit Label Dialog */}
      {editingLabel !== null && (
        <LabelDialog
          open={true}
          onClose={() => setEditingLabel(null)}
          onSave={(label) => updateLabel(editingLabel.dpIndex, editingLabel.labelIndex, label)}
          initialData={dataPoints[editingLabel.dpIndex].labels[editingLabel.labelIndex]}
          availableThemes={availableThemes}
          title="Edit Label"
        />
      )}
    </div>
  )
}

function DataPointDialog({
  open,
  onClose,
  onSave,
  initialData,
  title,
}: {
  open: boolean
  onClose: () => void
  onSave: (dataPoint: DataPoint) => void
  initialData?: DataPoint
  title: string
}) {
  const [usage, setUsage] = React.useState(initialData?.usage ?? "")

  React.useEffect(() => {
    if (open && initialData != null) {
      setUsage(initialData.usage)
    } else if (open && initialData == null) {
      setUsage("")
    }
  }, [open, initialData])

  const handleSave = () => {
    if (usage.trim().length === 0) return
    onSave({
      usage: usage.trim(),
      labels: initialData?.labels ?? [],
    })
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium">Usage *</label>
            <Select value={usage} onValueChange={setUsage}>
              <SelectTrigger>
                <SelectValue placeholder="Select usage" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recordList">Record List</SelectItem>
                <SelectItem value="lens">Lens</SelectItem>
                <SelectItem value="both">Both</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t pt-4">
          <DialogClose asChild>
            <Button variant="outline" type="button">
              Cancel
            </Button>
          </DialogClose>
          <Button onClick={handleSave} disabled={usage.trim().length === 0} type="button">
            {initialData != null ? "Save" : "Add"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function LabelDialog({
  open,
  onClose,
  onSave,
  initialData,
  availableThemes,
  title,
}: {
  open: boolean
  onClose: () => void
  onSave: (label: DataPointLabel) => void
  initialData?: DataPointLabel
  availableThemes: string[]
  title: string
}) {
  const [name, setName] = React.useState("")
  const [position, setPosition] = React.useState(0)
  const [showEvidenceThemes, setShowEvidenceThemes] = React.useState(false)
  const [selectedThemes, setSelectedThemes] = React.useState<string[]>([])

  React.useEffect(() => {
    if (open && initialData != null) {
      setName(initialData.name)
      setPosition(initialData.position)
      setShowEvidenceThemes(initialData.showEvidenceThemes)
      setSelectedThemes(initialData.themes)
    } else if (open && initialData == null) {
      setName("")
      setPosition(0)
      setShowEvidenceThemes(false)
      setSelectedThemes([])
    }
  }, [open, initialData])

  const toggleTheme = (theme: string) => {
    setSelectedThemes((prev) => (prev.includes(theme) ? prev.filter((t) => t !== theme) : [...prev, theme]))
  }

  const handleSave = () => {
    if (name.trim().length === 0) return
    onSave({
      name: name.trim(),
      position,
      showEvidenceThemes,
      themes: selectedThemes,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium">Name *</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Primary Label" />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">Position</label>
            <Input
              type="number"
              value={position}
              onChange={(e) => setPosition(Number.parseInt(e.target.value, 10) || 0)}
              placeholder="0"
            />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              checked={showEvidenceThemes}
              onCheckedChange={(checked) => setShowEvidenceThemes(checked === true)}
            />
            <label className="text-sm font-medium">Show Evidence Themes</label>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">Themes</label>
            {availableThemes.length === 0 ? (
              <p className="text-sm text-muted-foreground">No themes available</p>
            ) : (
              <div className="max-h-60 space-y-2 overflow-y-auto rounded border p-3">
                {availableThemes.map((theme) => (
                  <label key={theme} className="flex items-center gap-2">
                    <Checkbox checked={selectedThemes.includes(theme)} onCheckedChange={() => toggleTheme(theme)} />
                    <span className="text-sm">{theme}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t pt-4">
          <DialogClose asChild>
            <Button variant="outline" type="button">
              Cancel
            </Button>
          </DialogClose>
          <Button onClick={handleSave} disabled={name.trim().length === 0} type="button">
            {initialData != null ? "Save" : "Add"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
